<template>
    <section-wrapper
        v-if="investmentStrategySelected"
        :class="Section.INVESTMENT_STRATEGY.cssClassName"
        :this-section="Section.INVESTMENT_STRATEGY"
        :next-section="determineNextSectionAfterInvestmentStrategy"
        :section-end-button-label="sectionEndButtonLabel"
        :section-end-button-is-disabled="disableSectionButton"
        @sectionEndButtonClick="handleSectionEndButtonClick">
        <div class="row">
            <div class="col">
                <h2>{{ $t('investmentStrategyType.title') }}</h2>
                <div class="row">
                    <div class="col">
                        <p>{{ $t('investmentStrategyType.text', { investmentHorizon, yearString: investmentHorizon > 1 ? $t('common.yearsPreposition') : $t('common.year'),strategy: $t(`investmentStrategyType.${investmentStrategySelected.name}.title`), risk: $t(`riskToleranceEvaluation.riskDescription.${investmentStrategySelected.name}`) }) }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="investment-strategy__items d-none d-lg-flex">
            <radio-item
                :checked="investmentStrategySelected === InvestmentStrategyType.INCOME"
                :title="$t(`investmentStrategyType.${InvestmentStrategyType.INCOME.name}.title`)"
                :text="$t(`investmentStrategyType.${InvestmentStrategyType.INCOME.name}.text`)"
                @click="onSetInvestmentStrategySelected(InvestmentStrategyType.INCOME)" />
            <radio-item
                :checked="investmentStrategySelected === InvestmentStrategyType.CONSERVATIVE"
                :title="$t(`investmentStrategyType.${InvestmentStrategyType.CONSERVATIVE.name}.title`)"
                :text="$t(`investmentStrategyType.${InvestmentStrategyType.CONSERVATIVE.name}.text`)"
                @click="onSetInvestmentStrategySelected(InvestmentStrategyType.CONSERVATIVE)" />
            <radio-item
                :checked="investmentStrategySelected === InvestmentStrategyType.BALANCED"
                :title="$t(`investmentStrategyType.${InvestmentStrategyType.BALANCED.name}.title`)"
                :text="$t(`investmentStrategyType.${InvestmentStrategyType.BALANCED.name}.text`)"
                @click="onSetInvestmentStrategySelected(InvestmentStrategyType.BALANCED)" />
            <radio-item
                :checked="investmentStrategySelected === InvestmentStrategyType.GROWTH"
                :title="$t(`investmentStrategyType.${InvestmentStrategyType.GROWTH.name}.title`)"
                :text="$t(`investmentStrategyType.${InvestmentStrategyType.GROWTH.name}.text`)"
                @click="onSetInvestmentStrategySelected(InvestmentStrategyType.GROWTH)" />
            <radio-item
                :checked="investmentStrategySelected === InvestmentStrategyType.DYNAMIC"
                :title="$t(`investmentStrategyType.${InvestmentStrategyType.DYNAMIC.name}.title`)"
                :text="$t(`investmentStrategyType.${InvestmentStrategyType.DYNAMIC.name}.text`)"
                @click="onSetInvestmentStrategySelected(InvestmentStrategyType.DYNAMIC)" />
        </div>

        <radio-item-select
            class="investment-strategy__items d-lg-none"
            :selected="investmentStrategySelected"
            :options="mobileListElements"
            :is-collapsed="mobileListIsCollapsed"
            :list-text="$t('investmentStrategyType.mobileListText')"
            @toggleCollapse="mobileListIsCollapsed = !mobileListIsCollapsed"
            @onSelect="onSetInvestmentStrategySelected" />

        <div class="row">
            <div class="col">
                <transition name="fade">
                    <investment-strategy-change-notification
                        v-if="previousInvestmentStrategySelected && !this.viewportNoMatch"
                        tense="past" />
                </transition>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <investment-strategy-recommended-alert />
            </div>
        </div>

        <div class="row">
            <div class="col">
                <investment-strategy-chart />
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div class="investment-strategy__info">
                    <h2>{{ $t(`investmentStrategyType.${investmentStrategySelected.name}.info.title`) }}</h2>
                    <p class="investment-strategy__info-text">
                        {{ $t(`investmentStrategyType.${investmentStrategySelected.name}.info.textStandard`) }}
                    </p>
                    <p>
                        <span class="investment-strategy__info-text--bold">{{ $t(`investmentStrategyType.${investmentStrategySelected.name}.info.textBold`) }}</span>
                    </p>
                </div>
                <div class="investment-strategy__info-box d-lg-flex">
                    <div class="investment-strategy__return-value-box flex-fill">
                        <div class="investment-strategy__return-value">
                            {{ $t('investmentStrategyType.infoBox.returnValue',
                                  { value: this.$options.filters.percentage(investmentStrategyInfo.expectedYearlyReturn) })
                            }}
                        </div>
                        <div class="investment-strategy__return-value-text">
                            {{ $t('investmentStrategyType.infoBox.returnValueText') }}
                        </div>
                    </div>
                    <div class="investment-strategy__return-fluctuation-box d-flex flex-column flex-fill">
                        <div class="investment-strategy__return-fluctuation-title flex-fill">
                            {{ $t('investmentStrategyType.infoBox.returnFluctuation') }}
                        </div>
                        <div class="investment-strategy__return-fluctuation-values d-flex flex-row flex-fill">
                            <div class="investment-strategy__return-fluctuation-max d-flex flex-fill">
                                <div class="investment-strategy__return-fluctuation-icon">
                                    <Icon
                                        :type="'arrow-up'"
                                        :width="18"
                                        :height="18" />
                                </div>
                                <div class="investment-strategy__return-fluctuation-text">
                                    {{ investmentStrategyInfo.fluctuationMax | percentage }}
                                </div>
                            </div>
                            <div class="investment-strategy__return-fluctuation-min d-flex flex-fill">
                                <div class="investment-strategy__return-fluctuation-icon">
                                    <Icon
                                        :type="'arrow-down'"
                                        :width="18"
                                        :height="18" />
                                </div>
                                <div class="investment-strategy__return-fluctuation-text">
                                    {{ investmentStrategyInfo.fluctuationMin | percentage }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="investment-strategy__info-box-annotation">
                    {{ $t('investmentStrategyType.infoBox.annotation') }}
                </div>
            </div>
        </div>
    </section-wrapper>
</template>

<script>
import { InvestmentStrategyType } from '@/api/enums';
import investmentStrategyTypeApi from '@/api/investmentStrategyTypeApi';
import RadioItem from '@/components/common/RadioItem';
import RadioItemSelect from '@/components/RadioItemSelect';
import InvestmentStrategyChart from '@/components/InvestmentStrategyChart';
import InvestmentStrategyChangeNotification from '@/components/InvestmentStrategyChangeNotification';
import _ from 'lodash';
import InvestmentStrategyRecommendedAlert from './InvestmentStrategyRecommendedAlert';
import SectionWrapper from '@/components/common/SectionWrapper';
import { Section } from '@/enums';
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
    name: 'InvestmentStrategy',
    computed: {
        ...mapGetters([
            'determineNextSectionAfterInvestmentStrategy',
            'investmentStrategySelected',
            'viewportNoMatch',
            'previousInvestmentStrategySelected',
            'infoBoxTimeout']),
        ...mapState([
            'investmentStrategySelected',
            'investmentHorizon']),
        mobileListElements () {
            return Object.values(InvestmentStrategyType).map(value => {
                return {
                    key: value,
                    value: this.$t(`investmentStrategyType.${value.name}.title`),
                };
            });
        },
        disableSectionButton () {
            const notNullViewport = _.isNil(this.viewportNoMatch) ? false : this.viewportNoMatch;
            return !this.investmentStrategySelected || (!this.mobileListIsCollapsed && notNullViewport);
        },
        sectionEndButtonLabel () {
            return this.determineNextSectionAfterInvestmentStrategy === Section.THEME ? this.$t('investmentStrategyType.button.label.toTheme')
                : this.$t('investmentStrategyType.button.label.toQuestions');
        },
    },
    components: {
        RadioItem,
        RadioItemSelect,
        InvestmentStrategyRecommendedAlert,
        SectionWrapper,
        InvestmentStrategyChart,
        InvestmentStrategyChangeNotification,
    },
    methods: {
        ...mapActions(['setInvestmentStrategySelected', 'resetPreviousInvestmentStrategySelected', 'updateRiskToleranceWithSelectedStrategy']),
        ...mapGetters(['selectedLanguage']),
        fetchInvestmentStrategyInfo () {
            const language = this.selectedLanguage();
            investmentStrategyTypeApi.fetchStrategyByType(this.investmentStrategySelected, language).then(strategyInfo => {
                this.investmentStrategyInfo = strategyInfo;
            });
        },
        onSetInvestmentStrategySelected (type) {
            this.mobileListIsCollapsed = true;
            if (type !== this.investmentStrategySelected) {
                this.isInvestmentStrategyChangeManual = true; // to avoid that the watcher expands the dropdown list (mobile version) again
                this.setInvestmentStrategySelected(type);
            }

            this.clearInfoBoxTimeout();
            this.resetPreviousInvestmentStrategySelected();
        },
        clearInfoBoxTimeout () {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
        },
        handleSectionEndButtonClick () {
            if (this.determineNextSectionAfterInvestmentStrategy === Section.THEME) {
                this.updateRiskToleranceWithSelectedStrategy();
            }
        },
    },
    data () {
        return {
            InvestmentStrategyType,
            investmentStrategyInfo: {
                expectedYearlyReturn: null,
                fluctuationMin: null,
                fluctuationMax: null,
            },
            mobileListIsCollapsed: false,
            Section,
            isInvestmentStrategyChangeManual: false,
            timeout: null,
        };
    },
    watch: {
        investmentStrategySelected (newValue, oldValue) {
            this.fetchInvestmentStrategyInfo();

            if (this.isInvestmentStrategyChangeManual) {
                this.isInvestmentStrategyChangeManual = false;
                return;
            }

            this.mobileListIsCollapsed = false;

            if (oldValue && newValue && oldValue.name !== newValue.name) {
                this.clearInfoBoxTimeout();

                this.timeout = setTimeout(() => {
                    this.resetPreviousInvestmentStrategySelected();
                }, this.infoBoxTimeout);
            } else {
                this.clearInfoBoxTimeout();
                this.resetPreviousInvestmentStrategySelected();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.investment-strategy {
  .investment-strategy__items {
    margin-bottom: 20px;

    @include media-breakpoint-down(xs) {
      margin-bottom: 10px;
    }
  }

  .investment-strategy__info {
    @include media-breakpoint-up(lg) {
      margin-top: 15px;
    }

    h2 {
        color: $mb-grey5;
    }
  }

  .investment-strategy__info-text {
    @include font-copy();
    margin-bottom: 0;
  }

  .investment-strategy__info-text--bold {
    @include font-arial-bold();
    display: block;
    margin-bottom: 0;
  }

  .investment-strategy__info-box {
    @include font-migrosbank-bold();
    justify-content: center;
    padding: $grid-gutter-width / 2;
    color: $white;
    background-color: $mb-grey-12;
    margin: 20px $grid-negative-half-gutter 0 $grid-negative-half-gutter;

    @include media-breakpoint-down(xs) {
      font-size: 12px;
      margin: 15px 0 0;
    }
  }

  .investment-strategy__info-box-annotation {
    @include font-migrosbank();
    font-size: 12px;
    text-align: left;
    margin-top: 20px;

    @include media-breakpoint-down(xs) {
      font-size: 10px;
      text-align: left;
      margin-top: 15px;
    }
  }

  .investment-strategy__return-value-box {
    background-color: $mb-darkgreen;
    text-align: center;
    margin-bottom: 5px;
    padding: 10px 0;

    @include media-breakpoint-up(lg) {
      margin-bottom: 0;
      margin-right: 5px;
    }
  }

  .investment-strategy__return-value {
    font-size: 22px;

    @include media-breakpoint-down(xs) {
      font-size: 20px;
    }
  }

  .investment-strategy__return-value-text {
    @include font-migrosbank();
    margin-top: -8px;
  }

  .investment-strategy__return-fluctuation-title {
    background-color: $mb-grey5;
    line-height: 30px;
    margin-bottom: 2px;
    padding-left: 10px;

    @include media-breakpoint-down(xs) {
      text-align: center;
      padding-left: 0;
    }
  }

  .investment-strategy__return-fluctuation-max {
    background-color: $mb-violet;
    margin-right: 2px;
    position: relative;
  }

  .investment-strategy__return-fluctuation-min {
    background-color: $mb-orange;
    position: relative;
  }

  .investment-strategy__return-fluctuation-icon {
    padding-top: 6px;
    padding-left: 20px;

    @include media-breakpoint-down(xs) {
      padding-left: 10px;
    }
  }

  .investment-strategy__return-fluctuation-text {
    line-height: 30px;
    font-size: 18px;
    position: absolute;
    text-align: center;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
  }
}
</style>
